import React, { useEffect, useMemo, useState, useRef } from 'react';
import { AdminContentBlock, Spinner } from '../../../../common';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import { useTranslation } from 'react-i18next';
import {
  useGetCollidedSubjectsMutation,
  useGetReservationQuery,
  useGetSubjectsQuery,
  useSaveReservationMutation,
  useGetConflictsMutation,
} from '../../../../../redux/services/reservations';
import dayjs from 'dayjs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Form as AntdForm } from 'antd';
import { useSelector } from 'react-redux';
import { useGetTeachersQuery } from '../../../../../redux/services/users';
import { useGetRoomsForReservationQuery } from '../../../../../redux/services/rooms';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_ROOM_RESERVATIONS_ADMIN } from '../../../../../utils/constants/roles.js';
import ReservationForm from './ReservationForm.jsx';
import { reservationsOverviewUrl } from '../../../../../hooks/useReservationsOverviewMonth.jsx';

const ReservationEdit = ({ mode }) => {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const queryDay = searchParams.get('day');
  const queryRoomId = searchParams.get('room_id');
  const origin = searchParams.get('origin');
  const queryLastTime = searchParams.get('last_time');
  const { t } = useTranslation();
  const [form] = AntdForm.useForm();
  const navigate = useNavigate();
  const user = useSelector((reduxStore) => reduxStore.auth.user);
  const currentReservationUserId =
    user?.person_id === null || user?.person_id === undefined
      ? user?.id === null || user?.id === undefined
        ? undefined
        : String(user.id)
      : String(user.person_id);
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  const isRoomReservationsAdmin = useUserHasRole(ROLE_ROOM_RESERVATIONS_ADMIN);

  const pageTitle = isCreate
    ? t('Pridanie žiadosti o rezerváciu')
    : t('Úprava žiadosti o rezerváciu');

  useEffect(() => {
    document.title = pageTitle + ' - FRI CMS';
  }, []); // Runs on mount

  const { saveSuccess: showSaveSuccess, errorMessage } = useToastMessages();

  useEffect(() => {
    document.title = pageTitle;
  }, []); // Runs on mount

  const DEBOUNCE_MS = 500;
  const [getCollidedSubjects, { data: collidedSubjectsData }] =
    useGetCollidedSubjectsMutation();
  const [getConflicts] = useGetConflictsMutation();
  const [timeConflicts, setTimeConflicts] = useState(null);
  const [timeConflictsChecked, setTimeConflictsChecked] = useState(false);
  const subjectsTimer = useRef();

  const watchSubjectCode = AntdForm.useWatch('subject_code', form);
  // watch correct field names used in the form
  const watchDateFrom = AntdForm.useWatch('date_from', form);
  const watchDateTo = AntdForm.useWatch('date_to', form);
  const watchRoomId = AntdForm.useWatch('room_id', form);
  const watchAllDay = AntdForm.useWatch('all_day', form);

  const { data: reservationDetail, isLoading: isLoadingReservation } =
    useGetReservationQuery(id, { skip: !isEdit });

  const { data: teachers, isLoading: isLoadingTeachers } =
    useGetTeachersQuery();
  const { data: rooms, isLoading: isLoadingRooms } =
    useGetRoomsForReservationQuery();
  const { data: subjects, isLoading: isLoadingSubjects } =
    useGetSubjectsQuery();

  const [
    saveReservation,
    { isSuccess: isSaveSuccess, data: saveReservationResponse },
  ] = useSaveReservationMutation();

  // Fetch collided subjects when subject, from or to changes
  useEffect(() => {
    if (!watchSubjectCode || !watchDateFrom || !watchDateTo) return;

    // Skip when date fields have validation errors
    const hasDateErrors = form
      .getFieldsError(['date_from', 'date_to'])
      .some((field) => Array.isArray(field.errors) && field.errors.length > 0);
    if (hasDateErrors) return;

    // Manual validation mirroring form rules
    const fromDate = dayjs(watchDateFrom);
    const toDate = dayjs(watchDateTo);
    if (!fromDate.isValid() || !toDate.isValid()) return;
    if (watchAllDay) {
      if (toDate.startOf('day').isBefore(fromDate.startOf('day'))) return;
    } else {
      if (!toDate.isAfter(fromDate)) return;
    }

    // Debounce API call
    if (subjectsTimer.current) clearTimeout(subjectsTimer.current);
    subjectsTimer.current = setTimeout(() => {
      getCollidedSubjects({
        date_from: fromDate.set('second', 0).format(),
        date_to: toDate.set('second', 0).format(),
        code: watchSubjectCode,
      });
    }, DEBOUNCE_MS);

    return () => {
      if (subjectsTimer.current) clearTimeout(subjectsTimer.current);
    };
  }, [
    watchSubjectCode,
    watchDateFrom,
    watchDateTo,
    watchAllDay,
    form,
    getCollidedSubjects,
  ]);

  // Trigger conflicts check when room, from or to changes
  useEffect(() => {
    setTimeConflictsChecked(false);
  }, [watchRoomId, watchDateFrom, watchDateTo, watchAllDay]);

  // Fetch time conflicts when room, from or to changes
  useEffect(() => {
    const fetchConflicts = async () => {
      try {
        if (watchRoomId && watchDateFrom && watchDateTo) {
          // Skip conflict check when date validators report errors
          const hasDateErrors = form
            .getFieldsError(['date_from', 'date_to'])
            .some(
              (field) => Array.isArray(field.errors) && field.errors.length > 0,
            );
          if (hasDateErrors) {
            setTimeConflicts(null);
            setTimeConflictsChecked(false);
            return;
          }

          // Manual validation mirroring the form rules to avoid premature API calls
          const fromD = dayjs(watchDateFrom);
          const toD = dayjs(watchDateTo);
          if (!fromD.isValid() || !toD.isValid()) {
            setTimeConflicts(null);
            setTimeConflictsChecked(false);
            return;
          }
          if (watchAllDay) {
            if (toD.startOf('day').isBefore(fromD.startOf('day'))) {
              setTimeConflicts(null);
              setTimeConflictsChecked(false);
              return;
            }
          } else {
            if (!toD.isAfter(fromD)) {
              setTimeConflicts(null);
              setTimeConflictsChecked(false);
              return;
            }
          }

          const fromVal = watchAllDay ? fromD.startOf('day') : fromD;
          const toVal = watchAllDay ? toD.endOf('day') : toD;

          const result = await getConflicts({
            room_id: Number(watchRoomId),
            date_from: fromVal.set('second', 0).format(),
            date_to: toVal.set('second', 0).format(),
            ...(isEdit && reservationDetail?.id
              ? { reservation_id: reservationDetail.id }
              : {}),
          }).unwrap();
          setTimeConflicts(result ?? null);
          setTimeConflictsChecked(true);
        } else {
          setTimeConflicts(null);
          setTimeConflictsChecked(false);
        }
      } catch (e) {
        console.error('Chyba pri načítaní konfliktov rezervácií', e);
        setTimeConflicts(null);
        setTimeConflictsChecked(true);
      }
    };
    fetchConflicts();
  }, [
    watchRoomId,
    watchDateFrom,
    watchDateTo,
    watchAllDay,
    isEdit,
    reservationDetail,
    getConflicts,
  ]);

  // After successful save, show message and go back
  useEffect(() => {
    if (isSaveSuccess && saveReservationResponse) {
      if (saveReservationResponse.success) {
        showSaveSuccess();
        // return to the month of the saved reservation
        const savedDateFrom = form.getFieldValue('date_from');
        if (origin === 'cms') {
          navigate(reservationsOverviewUrl(routes.cmsReservationOverview, savedDateFrom));
        } else if (origin === 'public') {
          navigate(reservationsOverviewUrl(routes.reservationsOverview, savedDateFrom));
        } else {
          navigate(-1);
        }
      }
    }
  }, [isSaveSuccess, saveReservationResponse]);

  // Get department rooms for FormSelect
  const roomsOfDepartmentForSelect = useMemo(() => {
    if (!rooms) return [];
    return rooms
      .filter((room) => room.department_room === 'Yes')
      .map((room) => ({
        label: `${room.title} (${room.department_code})`,
        value: room.id,
      }));
  }, [rooms]);

   // Get other rooms for FormSelect
   // In create/edit mode, exclude other department rooms (marked by is_department_room)
   const roomsOtherForSelect = useMemo(() => {
     if (!rooms) return [];
     return rooms
       .filter((room) => {
         // In create/edit mode, exclude department rooms from other departments
          if (
            (isCreate || isEdit) &&
            !isRoomReservationsAdmin &&
            room.is_department_room === true
          ) {
           return false;
         }
         return room.department_room !== 'Yes';
       })
       .map((room) => ({
         label: `${room.title} (${room.department_code})`,
         value: room.id,
       }));
    }, [rooms, isCreate, isEdit, isRoomReservationsAdmin]);

  // Get teachers for FormSelect, sorted by last name
  const teachersForSelect = useMemo(() => {
    if (!teachers) return [];
    const options = teachers
      .map((teacher) => ({
        label: teacher.full_name,
        value: String(teacher.person_id),
        last_name: teacher.last_name,
      }))
      .sort((a, b) => a.last_name.localeCompare(b.last_name));

    if (
      currentReservationUserId &&
      !options.some((teacher) => teacher.value === currentReservationUserId)
    ) {
      options.unshift({
        label: user?.full_name || user?.fullName || user?.mail || currentReservationUserId,
        value: currentReservationUserId,
        last_name: user?.last_name || '',
      });
    }

    return options;
  }, [teachers, currentReservationUserId, user]);

  // Get subjects for FormSelect, sorted by name
  const subjectsForSelect = useMemo(() => {
    if (!subjects) return [];
    return subjects.map((subject) => ({
      label: subject.name,
      value: subject.code,
    }));
  }, [subjects]);

  // Helper to get a dayjs object for a given day and time
  function getDateWithTime(day, time) {
    const [hour, minute] = time.split(':').map(Number);
    return dayjs(day).hour(hour).minute(minute).second(0).millisecond(0);
  }

  const getNearestWholeHour = () => {
    const now = dayjs();
    const isAlreadyWholeHour = now.minute() === 0 && now.second() === 0;
    return now
      .startOf('hour')
      .add(isAlreadyWholeHour ? 0 : 1, 'hour')
      .second(0)
      .millisecond(0);
  };

  // Precompute create-mode defaults from query params
  const parsedDate = useMemo(() => {
    if (!queryDay) return null;
    const d = dayjs(queryDay, 'YYYY-MM-DD', true);
    return d.isValid() ? d.startOf('day') : null;
  }, [queryDay]);

  // If last_time is present and valid, use it for initial times, else use 8:00/12:00
  const isValidTime = queryLastTime && /^\d{2}:\d{2}$/.test(queryLastTime);
  const createModeFrom = useMemo(() => {
    if (parsedDate && parsedDate.isSame(dayjs(), 'day')) {
      return getNearestWholeHour();
    }

    if (parsedDate && isValidTime) {
      return getDateWithTime(parsedDate, queryLastTime).add(1, 'hour');
    }
    if (parsedDate)
      return parsedDate.hour(8).minute(0).second(0).millisecond(0);
    return dayjs().hour(8).minute(0).second(0).millisecond(0);
  }, [parsedDate, queryLastTime, isValidTime]);

  const createModeTo = useMemo(() => {
    if (parsedDate && parsedDate.isSame(dayjs(), 'day')) {
      return getNearestWholeHour().add(4, 'hour');
    }

    if (parsedDate && isValidTime) {
      return getDateWithTime(parsedDate, queryLastTime).add(1 + 4, 'hour');
    }
    if (parsedDate)
      return parsedDate.hour(12).minute(0).second(0).millisecond(0);
    return dayjs().hour(12).minute(0).second(0).millisecond(0);
  }, [parsedDate, queryLastTime, isValidTime]);

  const initialValues = {
    room_id: reservationDetail
      ? reservationDetail.room_id
      : queryRoomId
        ? +queryRoomId
        : undefined,
    date_from: reservationDetail
      ? dayjs(reservationDetail.date_from)
      : createModeFrom,
    date_to: reservationDetail
      ? dayjs(reservationDetail.date_to)
      : createModeTo,
    reason: reservationDetail?.reason,
    all_day: reservationDetail?.all_day ?? (isCreate ? false : undefined),
    created_for_id:
      reservationDetail?.created_for_id !== null &&
      reservationDetail?.created_for_id !== undefined
        ? String(reservationDetail.created_for_id)
        : currentReservationUserId,
    subject_code: reservationDetail?.subject_code,
    auto_accept: isEdit,
  };

  const isDatesValidForChecks = useMemo(() => {
    if (!watchDateFrom || !watchDateTo) return false;
    const hasDateErrors = form
      .getFieldsError(['date_from', 'date_to'])
      .some((field) => Array.isArray(field.errors) && field.errors.length > 0);
    if (hasDateErrors) return false;
    const fromD = dayjs(watchDateFrom);
    const toD = dayjs(watchDateTo);
    if (!fromD.isValid() || !toD.isValid()) return false;
    return watchAllDay
      ? !toD.startOf('day').isBefore(fromD.startOf('day'))
      : toD.isAfter(fromD);
  }, [watchDateFrom, watchDateTo, watchAllDay, form]);

  const handleFinish = (values) => {
    const conflictsArray = Array.isArray(timeConflicts)
      ? timeConflicts
      : timeConflicts && Array.isArray(timeConflicts.conflicts)
        ? timeConflicts.conflicts
        : timeConflicts
          ? [timeConflicts]
          : [];

    if (conflictsArray.length > 0) {
      errorMessage({
        message: t('Rezerváciu nie je možné uložiť'),
        description: t('Zvolený termín koliduje s existujúcou rezerváciou.'),
      });
      return;
    }

    let payload = { ...values };
    payload.date_from = payload.all_day
      ? dayjs(payload.date_from)
          .startOf('day')
          .set('second', 0)
          .format('YYYY-MM-DD HH:mm')
      : dayjs(payload.date_from).set('second', 0).format('YYYY-MM-DD HH:mm');
    payload.date_to = payload.all_day
      ? dayjs(payload.date_to)
          .endOf('day')
          .set('second', 0)
          .format('YYYY-MM-DD HH:mm')
      : dayjs(payload.date_to).set('second', 0).format('YYYY-MM-DD HH:mm');
    if (!isCreate) payload.id = reservationDetail.id;
    saveReservation(payload);
  };

  if (
    isLoadingReservation ||
    isLoadingTeachers ||
    isLoadingRooms ||
    isLoadingSubjects ||
    (isEdit && !reservationDetail)
  ) {
    return <Spinner />;
  } else {
    return (
      <>
        <AppBarCMS
          title={pageTitle}
          backUrl={-1}
          backUrlTooltip={t('Rezervácie')}
          permissionsBasic="reservations"
        />
        <div style={{ margin: '10px' }}>
          <AdminContentBlock
            icon="fa fa-sticky-note-o"
            link={routes.cmsReservations}
            title={pageTitle}
          >
            <ReservationForm
              form={form}
              initialValues={initialValues}
              onFinish={handleFinish}
              roomsOfDepartment={roomsOfDepartmentForSelect}
              roomsOther={roomsOtherForSelect}
              teachers={teachersForSelect}
              currentReservationUserId={currentReservationUserId}
              subjects={subjectsForSelect}
              isEdit={isEdit}
              isRoomReservationsAdmin={isRoomReservationsAdmin}
              collidedSubjects={
                isDatesValidForChecks ? collidedSubjectsData || [] : []
              }
              timeConflicts={timeConflicts}
              conflictsChecked={timeConflictsChecked}
            />
          </AdminContentBlock>
        </div>
      </>
    );
  }
};

export default ReservationEdit;
